/**
 * Adventure Ops - Microsoft Clarity Event Tracking Layer
 * Purpose:
 * - Track cart drawer opens by intent/source
 * - Track cart drawer interactions
 * - Track menu navigation interactions via site-specific adapters
 * - Track Algolia autocomplete search interactions
 * - Provide a central queue and event-name mapper for site tracking
 *
 * Open/close funnel events:
 * - cart_open with cart_open_type/cart_open_source tags
 * - cart_close with cart_close_source tag
 *
 * Menu funnel events:
 * - menu_open/menu_open_{path}
 * - menu_select_{path}
 * - menu_back_{path}
 * - menu_close
 * - menu_abandon
 * - menu_scrolled
 *
 * Search funnel events:
 * - search_overlay_displayed
 * - search_results_page_viewed
 * - search_product_selected
 * - search_suggestion_selected
 * - search_collection_selected
 * - search_view_all_clicked
 *
 * CONFIG quick guide:
 * - enabled: master kill switch for all custom Clarity events/tags.
 * - features.cart: enables cart-specific custom events/tags.
 * - features.menu: enables menu-specific custom events/tags.
 * - features.search: enables Algolia autocomplete custom events.
 * - debug.enabled: enables console logging for QA only.
 * - debug.events/debug.tags: filters event/tag console logs when debug is on.
 * - batchDelay: delay before queued events/tags retry or flush.
 * - maxEvents: flushes the event queue immediately once this count is reached.
 */

(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // CONFIG
  // ---------------------------------------------------------------------------

  const CONFIG = {
    enabled: true,
    features: {
      cart: true,
      menu: true,
      search: true
    },

    debug: {
      enabled: false,
      events: true,
      tags: false
    },

    // Event batching
    batchDelay: 800,
    maxEvents: 10
  };

  const FREE_SHIPPING_STATE_SELECTOR = "[data-cart-free-shipping-state]";
  const MENU_PATH_SEPARATOR = "_";
  const SEARCH_OVERLAY_SELECTOR = ".aa-Panel";
  const SEARCH_OVERLAY_CONTENT_SELECTOR = ".aa-Item, .aa-NoResultsHeader";
  const SEARCH_PRODUCT_SELECTOR = ".aa-ProductItem";
  const SEARCH_VIEW_ALL_SELECTOR = ".aa-SeeAllBtn";
  const SEARCH_SUGGESTION_SOURCE_SELECTOR = '[data-autocomplete-source-id="querySuggestionsPlugin"]';
  const SEARCH_COLLECTION_SOURCE_SELECTOR = '[data-autocomplete-source-id="collections"]';

  // ---------------------------------------------------------------------------
  // EVENT QUEUE
  // ---------------------------------------------------------------------------

  let eventQueue = [];
  let batchTimer = null;
  let pendingTags = {};
  let tagTimer = null;
  let menuOpenCycle = null;
  let searchOverlayDisplayed = false;
  let searchOverlayObserver = null;

  function log(type) {
    if (!CONFIG.debug || !CONFIG.debug.enabled) return;
    if (CONFIG.debug[type] === false) return;

    const args = Array.prototype.slice.call(arguments, 1);
    console.log.apply(console, ["[AO Clarity]"].concat(args));
  }

  function logGeneral() {
    log.apply(null, ["events"].concat(Array.from(arguments)));
  }

  function logQueued() {
    log.apply(null, ["events"].concat(Array.from(arguments)));
  }

  function logSent() {
    log.apply(null, ["events"].concat(Array.from(arguments)));
  }

  function logEvent() {
    log.apply(null, ["events"].concat(Array.from(arguments)));
  }

  function logTag() {
    log.apply(null, ["tags"].concat(Array.from(arguments)));
  }

  function isClarityReady() {
    return typeof window.clarity === "function";
  }

  function isTrackingEnabled(feature) {
    if (!CONFIG.enabled) return false;
    if (!feature) return true;

    return !CONFIG.features || CONFIG.features[feature] !== false;
  }

  function sendBatch() {
    if (!eventQueue.length) return;

    if (!isTrackingEnabled()) {
      eventQueue = [];
      return;
    }

    // If Clarity has not loaded yet, retry shortly.
    if (!isClarityReady()) {
      clearTimeout(batchTimer);
      batchTimer = setTimeout(sendBatch, CONFIG.batchDelay);
      return;
    }

    const events = eventQueue.slice();
    eventQueue = [];

    events.forEach(function (eventName) {
      try {
        window.clarity("event", eventName);
        logSent("Sent:", eventName);
      } catch (error) {
        logSent("Send failed:", eventName, error);
      }
    });
  }

  function sendTags() {
    const keys = Object.keys(pendingTags);
    if (!keys.length) return;

    if (!isTrackingEnabled()) {
      pendingTags = {};
      return;
    }

    if (!isClarityReady()) {
      clearTimeout(tagTimer);
      tagTimer = setTimeout(sendTags, CONFIG.batchDelay);
      return;
    }

    const tags = pendingTags;
    pendingTags = {};

    keys.forEach(function (key) {
      const value = tags[key];
      if (value === undefined || value === null) return;

      try {
        window.clarity("set", key, String(value));
        logTag("Tag:", key, String(value));
      } catch (error) {
        logTag("Tag failed:", key, String(value), error);
      }
    });
  }

  function track(eventName) {
    if (!isTrackingEnabled()) return;
    if (!eventName) return;

    // Prevent duplicate events inside the current batch window.
    if (eventQueue.indexOf(eventName) !== -1) return;

    eventQueue.push(eventName);
    logQueued("Queued:", eventName);

    if (eventQueue.length >= CONFIG.maxEvents) {
      sendBatch();
      return;
    }

    clearTimeout(batchTimer);
    batchTimer = setTimeout(sendBatch, CONFIG.batchDelay);
  }

  // Expose for console QA:
  // clarityTrack("test_event")
  window.clarityTrack = track;

  window.aoClarity = window.aoClarity || {};
  window.aoClarity.config = CONFIG;
  window.aoClarity.track = track;
  window.aoClarity.setTags = setTags;
  window.aoClarity.trackCartEvent = trackCartEvent;
  window.aoClarity.trackCartDrawerState = trackCartDrawerState;
  window.aoClarity.setCartState = setCartState;
  window.aoClarity.trackMenuEvent = trackMenuEvent;
  window.aoClarity.setMenuState = setMenuState;
  window.aoClarity.trackSearchEvent = trackSearchEvent;

  function setTags(tags) {
    if (!isTrackingEnabled()) return;
    if (!tags || typeof tags !== "object") return;

    Object.keys(tags).forEach(function (key) {
      pendingTags[key] = tags[key];
    });

    sendTags();
  }

  function setCartState(state) {
    if (!isTrackingEnabled("cart")) return;

    const cartState = state || {};

    setTags({
      cart_surface: cartState.surface,
      cart_free_shipping_state: cartState.freeShippingState,
      cart_value_band: cartState.valueBand,
      cart_item_count: cartState.itemCount,
      cart_total: cartState.total,
      cart_has_cross_sells: cartState.hasCrossSells,
      cart_has_heavy_bulky: cartState.hasHeavyBulky,
      cart_has_dropship: cartState.hasDropship,
      cart_pricing_mix: cartState.pricingMix
    });
  }

  function normalizeMenuPath(path) {
    if (Array.isArray(path)) {
      return path
        .filter(Boolean)
        .map(function (part) {
          return String(part).trim();
        })
        .filter(Boolean)
        .join(MENU_PATH_SEPARATOR);
    }

    return String(path || "")
      .trim()
      .replace(/\s*>\s*/g, MENU_PATH_SEPARATOR)
      .replace(/-+/g, MENU_PATH_SEPARATOR)
      .replace(/[^a-zA-Z0-9_]+/g, MENU_PATH_SEPARATOR)
      .replace(/_+/g, MENU_PATH_SEPARATOR)
      .replace(/^_+|_+$/g, "")
      .toLowerCase();
  }

  function getMenuDepth(path) {
    const normalizedPath = normalizeMenuPath(path);
    if (!normalizedPath) return 0;

    return normalizedPath.split(MENU_PATH_SEPARATOR).filter(Boolean).length - 1;
  }

  function getMenuTimeOpenBand() {
    if (!menuOpenCycle || !menuOpenCycle.openedAt) return "unknown";

    const secondsOpen = Math.round((Date.now() - menuOpenCycle.openedAt) / 1000);

    if (secondsOpen <= 2) return "0_2s";
    if (secondsOpen <= 5) return "3_5s";
    if (secondsOpen <= 10) return "6_10s";

    return "10s_plus";
  }

  function getMenuEventName(action, path) {
    const normalizedPath = normalizeMenuPath(path);

    if (action === "open") {
      return normalizedPath && normalizedPath !== "root" ? "menu_open_" + normalizedPath : "menu_open";
    }

    if (action === "select" && normalizedPath) {
      return "menu_select_" + normalizedPath;
    }

    if (action === "back" && normalizedPath) {
      return "menu_back_" + normalizedPath;
    }

    if (action === "close") {
      return "menu_close";
    }

    if (action === "abandon") {
      return "menu_abandon";
    }

    if (action === "scrolled") {
      return "menu_scrolled";
    }

    return null;
  }

  function setMenuState(state) {
    if (!isTrackingEnabled("menu")) return;

    const menuState = state || {};

    setTags({
      menu_used: menuState.used,
      menu_surface: menuState.surface,
      menu_open_source: menuState.openSource,
      menu_close_source: menuState.closeSource,
      menu_has_below_fold_items: menuState.hasBelowFoldItems,
      menu_scrolled: menuState.scrolled,
      menu_time_open_band: menuState.timeOpenBand
    });
  }

  function trackMenuEvent(action, detail) {
    if (!isTrackingEnabled("menu")) return;

    const details = detail || {};
    const normalizedPath = normalizeMenuPath(details.path);
    const depth = details.depth !== undefined ? details.depth : getMenuDepth(normalizedPath);
    const eventName = getMenuEventName(action, normalizedPath);

    if (!eventName) return;

    if (action === "open" && !menuOpenCycle) {
      menuOpenCycle = {
        openedAt: Date.now(),
        selectedThisOpen: false,
        scrolled: false,
        depthReached: depth,
        lastPath: normalizedPath,
        surface: details.surface
      };
    } else if (menuOpenCycle) {
      menuOpenCycle.lastPath = normalizedPath || menuOpenCycle.lastPath;
      menuOpenCycle.surface = details.surface || menuOpenCycle.surface;
      menuOpenCycle.depthReached = Math.max(menuOpenCycle.depthReached || 0, depth);
    }

    if (action === "select" && menuOpenCycle) {
      menuOpenCycle.selectedThisOpen = true;
    }

    if (action === "scrolled" && menuOpenCycle) {
      menuOpenCycle.scrolled = true;
    }

    setMenuState({
      used: true,
      surface: details.surface || (menuOpenCycle && menuOpenCycle.surface),
      openSource: details.openSource,
      closeSource: action === "close" || action === "abandon" ? details.closeSource : undefined,
      path: normalizedPath,
      lastPath: (menuOpenCycle && menuOpenCycle.lastPath) || normalizedPath,
      label: details.label,
      depth: depth,
      depthReached: menuOpenCycle ? menuOpenCycle.depthReached : depth,
      destination: details.destination,
      linkType: details.linkType,
      selectedThisOpen: menuOpenCycle ? menuOpenCycle.selectedThisOpen : action === "select",
      hasBelowFoldItems: details.hasBelowFoldItems,
      scrolled: menuOpenCycle ? menuOpenCycle.scrolled : details.scrolled,
      timeOpenBand: action === "close" || action === "abandon" ? getMenuTimeOpenBand() : undefined
    });

    logEvent("Event:", eventName);
    track(eventName);

    if (action === "close" && menuOpenCycle) {
      if (!menuOpenCycle.selectedThisOpen) {
        const abandonEventName = getMenuEventName("abandon");

        setMenuState({
          used: true,
          surface: details.surface || menuOpenCycle.surface,
          closeSource: details.closeSource,
          lastPath: menuOpenCycle.lastPath,
          depthReached: menuOpenCycle.depthReached,
          selectedThisOpen: false,
          hasBelowFoldItems: details.hasBelowFoldItems,
          scrolled: menuOpenCycle.scrolled,
          timeOpenBand: getMenuTimeOpenBand()
        });

        logEvent("Event:", abandonEventName);
        track(abandonEventName);
      }

      menuOpenCycle = null;
    }
  }

  function trackCartEvent(action, detail) {
    if (!isTrackingEnabled("cart")) return;

    const details = detail || {};

    switch (action) {
      case "open": {
        const type = details.type || "unknown";
        const source = details.source || "unknown";
        const surface = details.surface || "unknown";

        setTags({
          cart_surface: surface,
          cart_open_type: type,
          cart_open_source: source
        });

        logEvent("Event:", "cart_open");
        track("cart_open");
        break;
      }
      case "remove":
        logEvent("Event:", "cart_item_removed");
        track("cart_item_removed");
        break;
      case "quantity":
        if (details.direction === "increase") {
          logEvent("Event:", "cart_qty_increased");
          track("cart_qty_increased");
        } else if (details.direction === "decrease") {
          logEvent("Event:", "cart_qty_decreased");
          track("cart_qty_decreased");
        } else {
          logEvent("Event:", "cart_qty_changed");
          track("cart_qty_changed");
        }
        break;
      case "checkout":
        logEvent("Event:", "cart_checkout_click");
        track("cart_checkout_click");
        break;
      case "close":
        if (details.source === "icon") {
          setTags({ cart_close_source: "icon" });
          logEvent("Event:", "cart_close");
          track("cart_close");
        } else if (details.source === "overlay") {
          setTags({ cart_close_source: "overlay" });
          logEvent("Event:", "cart_close");
          track("cart_close");
        }
        break;
      case "cross_sell_open":
        logEvent("Event:", "cart_cross_sell_open");
        track("cart_cross_sell_open");
        break;
      case "cross_sell_view":
        logEvent("Event:", "cart_cross_sell_view");
        track("cart_cross_sell_view");
        break;
      case "cross_sell_impression":
        logEvent("Event:", "cart_cross_sell_impression");
        track("cart_cross_sell_impression");
        break;
      case "cross_sell_atc":
        logEvent("Event:", "cart_cross_sell_atc");
        track("cart_cross_sell_atc");
        break;
      case "cross_sell_nav":
        logEvent("Event:", "cart_cross_sell_nav");
        track("cart_cross_sell_nav");
        break;
      case "cross_sell_variant_change":
        logEvent("Event:", "cart_cross_sell_variant_change");
        track("cart_cross_sell_variant_change");
        break;
      case "cross_sell_pdp_open":
        logEvent("Event:", "cart_cross_sell_pdp_open");
        track("cart_cross_sell_pdp_open");
        break;
      case "cross_sell_close_without_atc":
        logEvent("Event:", "cart_cross_sell_close_without_atc");
        track("cart_cross_sell_close_without_atc");
        break;
      default:
        if (action) {
          logEvent("Event:", action);
          track(action);
        }
    }
  }

  function trackCartDrawerState(root) {
    if (!isTrackingEnabled("cart")) return;

    const stateEl = root
      ? root.querySelector(FREE_SHIPPING_STATE_SELECTOR)
      : document.querySelector(FREE_SHIPPING_STATE_SELECTOR);

    if (!stateEl) return;

    if (stateEl.dataset.itemCount === "0") {
      logEvent("Event:", "cart_emptied");
      track("cart_emptied");
      return;
    }

    if (stateEl.dataset.hasHeavyBulky === "true") {
      logEvent("Event:", "cart_free_shipping_blocked_heavy_bulky");
      track("cart_free_shipping_blocked_heavy_bulky");
      return;
    }

    if (stateEl.dataset.freeShippingUnlocked === "true") {
      logEvent("Event:", "cart_free_shipping_threshold_met");
      track("cart_free_shipping_threshold_met");
    } else {
      logEvent("Event:", "cart_free_shipping_threshold_not_met");
      track("cart_free_shipping_threshold_not_met");
    }
  }

  function trackSearchEvent(action) {
    if (!isTrackingEnabled("search")) return;

    const eventNames = {
      overlay_displayed: "search_overlay_displayed",
      results_page_viewed: "search_results_page_viewed",
      product_selected: "search_product_selected",
      suggestion_selected: "search_suggestion_selected",
      collection_selected: "search_collection_selected",
      view_all_clicked: "search_view_all_clicked"
    };
    const eventName = eventNames[action];

    if (!eventName) return;

    logEvent("Event:", eventName);
    track(eventName);

    // Send search events immediately so navigation clicks are not lost.
    sendBatch();
  }

  function isSearchResultsPage() {
    const pathname = window.location && window.location.pathname;
    const normalizedPath = String(pathname || "").replace(/\/+$/, "");

    return normalizedPath === "/search" || normalizedPath.endsWith("/search");
  }

  function trackSearchResultsPageView() {
    if (isSearchResultsPage()) {
      trackSearchEvent("results_page_viewed");
    }
  }

  function isSearchOverlayDisplayed() {
    const overlay = document.querySelector(SEARCH_OVERLAY_SELECTOR);

    if (!overlay || overlay.getAttribute("aria-hidden") === "true") return false;
    if (!overlay.querySelector(SEARCH_OVERLAY_CONTENT_SELECTOR)) return false;

    return typeof overlay.getClientRects !== "function" || overlay.getClientRects().length > 0;
  }

  function trackSearchOverlayDisplayed() {
    if (searchOverlayDisplayed || !isSearchOverlayDisplayed()) return false;

    searchOverlayDisplayed = true;
    trackSearchEvent("overlay_displayed");

    if (searchOverlayObserver) {
      searchOverlayObserver.disconnect();
      searchOverlayObserver = null;
    }

    return true;
  }

  function observeSearchOverlay() {
    if (trackSearchOverlayDisplayed()) return;
    if (!document.body || typeof window.MutationObserver !== "function") return;

    searchOverlayObserver = new window.MutationObserver(trackSearchOverlayDisplayed);
    searchOverlayObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function getSearchAction(target) {
    if (!target || typeof target.closest !== "function") return null;

    if (target.closest(SEARCH_VIEW_ALL_SELECTOR)) return "view_all_clicked";
    if (target.closest(SEARCH_PRODUCT_SELECTOR)) return "product_selected";
    if (target.closest(SEARCH_SUGGESTION_SOURCE_SELECTOR)) return "suggestion_selected";
    if (target.closest(SEARCH_COLLECTION_SOURCE_SELECTOR)) return "collection_selected";

    return null;
  }

  function handleSearchClick(event) {
    const action = getSearchAction(event.target);
    if (action) trackSearchEvent(action);
  }

  // ---------------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------------

  function init() {
    logGeneral("Initialising Clarity events");
    trackSearchResultsPageView();
    observeSearchOverlay();
    document.addEventListener("click", handleSearchClick, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
